import { useState, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { consumePendingPrompt } from "../stores/aiChatSlice";
import { useAIChat } from "../hooks/useAIChat";
import { useVoice } from "../hooks/useVoice";
import { MicButton, VoiceStatusStrip } from "./VoiceControls";

// ---------------------------------------------------------------------------
// Composer — multiline input + mic + send button. Enter to send, Shift+Enter
// for newline. Disables when no model is picked or a stream is in flight.
// Typed text goes through the voice layer's `sendText` so "Read aloud" can
// speak the answer; the mic button records and sends on its own.
// ---------------------------------------------------------------------------

export default function AIComposer() {
  const [text, setText] = useState("");
  const { cancel } = useAIChat();
  const { sendText, phase: voicePhase, stopAll: stopVoice } = useVoice();
  const voiceBusy = voicePhase !== "idle";
  const isStreaming = useAppSelector((s) => s.aiChat.isStreaming);
  const hasModel = useAppSelector((s) => s.aiChat.currentModelId !== null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const dispatch = useAppDispatch();
  const pendingPrompt = useAppSelector((s) => s.aiChat.pendingPrompt);

  // A prompt queued from outside the panel (the automation builder's "Build
  // with AI" bar) goes out through the same pipeline as a typed one.
  useEffect(() => {
    if (!pendingPrompt || isStreaming || !hasModel) return;
    dispatch(consumePendingPrompt());
    void sendText(pendingPrompt);
  }, [pendingPrompt, isStreaming, hasModel, dispatch, sendText]);

  // Auto-resize textarea to content.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [text]);

  const handleSend = () => {
    if (!text.trim() || isStreaming || !hasModel) return;
    const t = text;
    setText("");
    void sendText(t);
  };

  return (
    <div
      style={{
        borderTop: "1px solid var(--color-black-3, #e5e7eb)",
        padding: 10,
        background: "var(--color-bg, #fff)",
      }}
    >
      <VoiceStatusStrip />
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
        }}
      >
        <MicButton disabled={!hasModel || isStreaming} />
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={
            hasModel ? "Ask anything about your CRM…" : "Pick a model first"
          }
          rows={1}
          disabled={!hasModel}
          style={{
            flex: 1,
            resize: "none",
            border: "1px solid var(--color-black-3, #e5e7eb)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 13,
            fontFamily: "inherit",
            outline: "none",
            background: hasModel ? "#fff" : "#f3f4f6",
            color: "var(--color-text-primary, #1a1a1a)",
            lineHeight: 1.4,
            maxHeight: 160,
          }}
        />
        {isStreaming || voiceBusy ? (
          <button
            onClick={() => {
              cancel();
              stopVoice();
            }}
            style={{
              padding: "8px 12px",
              background: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #fecaca",
              borderRadius: 8,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Stop
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={!text.trim() || !hasModel}
            style={{
              padding: "8px 14px",
              background:
                text.trim() && hasModel
                  ? "var(--color-success, #156548)"
                  : "#9ca3af",
              color: "#fff",
              border: 0,
              borderRadius: 8,
              fontSize: 13,
              cursor: text.trim() && hasModel ? "pointer" : "not-allowed",
            }}
          >
            Send
          </button>
        )}
      </div>
      <div
        style={{
          fontSize: 10,
          opacity: 0.5,
          marginTop: 6,
          textAlign: "right",
        }}
      >
        Enter to send · Shift+Enter for newline · mic = live voice call · ⌘K to toggle
      </div>
    </div>
  );
}
